<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import InternalChatPanel from './InternalChatPanel.vue';
import AdsConversationReferralPortal from '../ads/components/AdsConversationReferralPortal.vue';
import AdsSidebarPortal from '../ads/components/AdsSidebarPortal.vue';
import AdsConversationTrackingPortal from '../ads/components/AdsConversationTrackingPortal.vue';
import WahaInboxStatusReconcile from '../waha/WahaInboxStatusReconcile.vue';
import { useRastroEnabled } from '../ads/useRastro';
import {
  INTERNAL_CHAT_PERMISSIONS,
  closeInternalChatPanel,
} from './sidepanelUi';

const store = useStore();
const route = useRoute();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { checkPermissions } = usePolicy();
const { width: windowWidth } = useWindowSize();
const panelRef = ref(null);
const hasFetchedChannels = ref(false);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const isInternalChatRoute = computed(() =>
  String(route.name || '').startsWith('internal_chat')
);

const isFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.INTERNAL_CHAT
  )
);
const isRastroEnabled = useRastroEnabled();

const hasPermission = computed(() =>
  checkPermissions(INTERNAL_CHAT_PERMISSIONS)
);

const canUseInternalChat = computed(
  () => isFeatureEnabled.value && hasPermission.value
);

const isPanelOpen = computed(
  () => !!uiSettings.value?.is_internal_chat_panel_open
);

const shouldShowPanel = computed(
  () =>
    canUseInternalChat.value && isPanelOpen.value && !isInternalChatRoute.value
);

async function prefetchChannels() {
  if (!canUseInternalChat.value || hasFetchedChannels.value) return;

  try {
    await store.dispatch('internalChat/get');
    store.dispatch('agents/get');
    store.dispatch('teams/get');
    hasFetchedChannels.value = true;
  } catch {
    useAlert(t('INTERNAL_CHAT.ERRORS.FETCH_CHANNELS'));
  }
}

async function restorePanelChannel() {
  await prefetchChannels();
  await nextTick();
  panelRef.value?.restoreLastChannel?.();
}

onMounted(() => {
  prefetchChannels();
});

watch(canUseInternalChat, enabled => {
  if (enabled) prefetchChannels();
});

watch(
  shouldShowPanel,
  isOpen => {
    if (isOpen) {
      restorePanelChannel();
    } else if (store.getters['internalChat/getActiveChannelId']) {
      store.dispatch('internalChat/setActiveChannel', null);
    }
  },
  { immediate: true }
);

// Close when contact/copilot open elsewhere (mutual exclusion without touching core closes)
watch(
  () => [
    uiSettings.value?.is_contact_sidebar_open,
    uiSettings.value?.is_copilot_panel_open,
  ],
  ([contactOpen, copilotOpen]) => {
    if ((contactOpen || copilotOpen) && isPanelOpen.value) {
      closeInternalChatPanel(updateUISettings);
    }
  }
);

watch(isInternalChatRoute, onInternalChat => {
  if (onInternalChat && isPanelOpen.value) {
    closeInternalChatPanel(updateUISettings);
  }
});

const closeOnSmallScreen = () => {
  if (isSmallScreen.value && isPanelOpen.value) {
    closeInternalChatPanel(updateUISettings);
  }
};
</script>

<template>
  <WahaInboxStatusReconcile />
  <AdsSidebarPortal v-if="isRastroEnabled" />
  <AdsConversationReferralPortal v-if="isRastroEnabled" />
  <AdsConversationTrackingPortal v-if="isRastroEnabled" />
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="[
      () => closeOnSmallScreen(),
      {
        ignore: [
          'dialog',
          'dialog::backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
          '[data-alibsoft-internal-chat-switch]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none md:flex"
  >
    <InternalChatPanel ref="panelRef" />
  </div>
</template>
